import { useSyncExternalStore } from 'react'
import { supabase } from '../lib/supabase'

/**
 * The three things every Pro surface needs from auth:
 *  - token:   Supabase access token, the Bearer for /pro/* calls
 *  - userId:  Supabase user id, the React Query key AND the RevenueCat
 *             app_user_id, so purchase and status must both gate on it
 *  - loading: true only while an initial session read is in flight
 */
export interface SupabaseTokenState {
  token: string | null
  userId: string | null
  loading: boolean
  /**
   * The session is a Supabase ANONYMOUS user. The collection layer treats it
   * as a guest (collectionOwnerOf). Absent reads as false.
   */
  anonymous?: boolean
}

// Whose collection this auth state is (a permanent user's id, null for a
// guest, undefined while loading). Lives in lib/collectionOwner so the rule
// survives a test double of this module; re-exported here for callers.
export { collectionOwnerOf } from '../lib/collectionOwner'

// ONE store for the whole app, read through useSyncExternalStore.
//
// This was per-component useState plus a per-component onAuthStateChange
// subscription, so every consumer held its own COPY of the same auth state. The
// Deck Chat has several consumers at once: the dock (or the inline panel) for
// its quota pill, DeckChat for the composer gate and the paywall, and
// useProStatus internally for each of those. Independent copies settle in
// whatever order React happens to flush them, so a host could believe the user
// was signed in for a frame that the chat inside it did not - the kind of
// split-brain that renders a quota pill above a sign-in gate.
//
// A single module-level snapshot makes that impossible: every consumer in a
// render pass reads the same object, and one Supabase subscription feeds them
// all. The hook signature is unchanged, so no call site had to move.
const SIGNED_OUT: SupabaseTokenState = { token: null, userId: null, loading: false }

// If Supabase is not configured the client is null and no token can ever
// arrive, so loading starts false: that keeps `enabled` correctly false on the
// first read instead of gating queries off forever.
let state: SupabaseTokenState = supabase
  ? { token: null, userId: null, loading: true }
  : SIGNED_OUT

const listeners = new Set<() => void>()
let started = false

// True when the initial getSession() FAILED (rejected, or resolved with an
// `error`) and no AUTHORITATIVE auth event has answered since. Kept OUT of
// SupabaseTokenState on purpose: that snapshot maps a failed read to
// SIGNED_OUT, which is the right answer for every existing caller (a gate off
// forever would be worse), so its shape and values do not change. The one
// caller that must not treat a failed read as a confirmed anonymous visitor
// (the fail-closed sponsor gate in src/lib/ads.ts) reads this through
// useSupabaseAuthFailed().
//
// Two facts, combined: `readFailed` is sticky once the read fails, whichever
// order it lands in relative to events, and `authoritativeSeen` records an
// event that is real evidence of the session state.
let readFailed = false
let authoritativeSeen = false
let authFailed = false

// Events that are Supabase positively answering. INITIAL_SESSION is
// deliberately NOT here: supabase-js emits it with a null session when its
// own session load errored, so a null INITIAL_SESSION proves nothing. It
// still counts when it carries a real session (see isAuthoritative).
const AUTHORITATIVE_EVENTS = new Set(['SIGNED_IN', 'SIGNED_OUT', 'TOKEN_REFRESHED', 'USER_UPDATED'])

function isAuthoritative(event: string, session: unknown): boolean {
  return session != null || AUTHORITATIVE_EVENTS.has(event)
}

function notifyAll() {
  for (const listener of listeners) listener()
}

/** Recompute authFailed; true when it changed (the caller notifies). */
function recomputeAuthFailed(): boolean {
  const next = readFailed && !authoritativeSeen
  if (next === authFailed) return false
  authFailed = next
  return true
}
// Bumped by every auth EVENT. The initial getSession() read captures it before
// it starts and checks it on resolution: an event that landed while that read
// was in flight is NEWER information, so publishing the read's result on top of
// it would silently rewind the user (sign in during a slow cold read, get
// signed out again when it lands).
let authGeneration = 0

function publish(next: SupabaseTokenState) {
  // Identity changes only when a VALUE changes: useSyncExternalStore re-renders
  // on every new getSnapshot reference, and re-rendering every Pro surface for a
  // token refresh that changed nothing is pure waste.
  if (
    next.token === state.token &&
    next.userId === state.userId &&
    next.loading === state.loading &&
    !!next.anonymous === !!state.anonymous
  ) {
    return
  }
  state = next
  for (const listener of listeners) listener()
}

// Only present when true, so a permanent user's snapshot keeps its old shape.
function anonymousFlag(session: { user?: { is_anonymous?: boolean } | null } | null | undefined): { anonymous?: true } {
  return session?.user?.is_anonymous ? { anonymous: true } : {}
}

function start() {
  if (started || !supabase) return
  started = true

  const readGeneration = authGeneration

  // Retires the initial-read flag WITHOUT touching identity. Used when an auth
  // event overtook the read: that event owns token/userId now, but it left
  // `loading` alone (it cannot know whether the initial read is done), so
  // something still has to turn it off or every Pro query stays disabled.
  const finishSupersededRead = () => {
    publish({ ...state, loading: false })
  }

  // A failed session read is treated as "signed out, done loading" rather
  // than left pending: an unresolved loading flag would gate the status
  // query off for the rest of the page's life. Unless an event beat it here,
  // in which case a real session may exist and only the flag is ours to drop.
  // A read that RESOLVES with an `error` (an expired session that cannot
  // refresh, a network failure) takes this same path as a rejection.
  const onReadFailed = () => {
    readFailed = true
    // Before publish, so a consumer re-rendered by the publish reads both in
    // step; notified again after in case publish changed nothing.
    const changed = recomputeAuthFailed()
    if (authGeneration !== readGeneration) finishSupersededRead()
    else publish(SIGNED_OUT)
    if (changed) notifyAll()
  }

  supabase.auth
    .getSession()
    .then(({ data, error }) => {
      if (error) {
        onReadFailed()
        return
      }
      if (authGeneration !== readGeneration) {
        finishSupersededRead()
        return
      }
      publish({
        token: data.session?.access_token ?? null,
        userId: data.session?.user?.id ?? null,
        loading: false,
        ...anonymousFlag(data.session),
      })
    })
    .catch(onReadFailed)

  // Never unsubscribed. Auth is app-global and this store outlives every
  // component that reads it, so dropping the subscription when the last
  // consumer unmounts would only mean missing a sign-in that happened while no
  // Pro surface was on screen. One subscription per page load instead of one
  // per mounted component is the whole point.
  supabase.auth.onAuthStateChange((event, session) => {
    // Bump BEFORE publishing so an in-flight initial read can never win a race
    // against this event, whatever order the promise callbacks run in. Every
    // event bumps, as before: the token snapshot's behavior is unchanged.
    authGeneration += 1
    // Only an authoritative event clears a failed read (or pre-empts one
    // still in flight). A null INITIAL_SESSION does neither.
    if (isAuthoritative(event, session)) authoritativeSeen = true
    const changed = recomputeAuthFailed()
    publish({
      token: session?.access_token ?? null,
      userId: session?.user?.id ?? null,
      // Deliberately NOT forced to false: getSession owns the initial-read
      // flag, and supabase-js emits INITIAL_SESSION before that read resolves.
      loading: state.loading,
      ...anonymousFlag(session),
    })
    if (changed) notifyAll()
  })
}

function subscribe(onStoreChange: () => void) {
  listeners.add(onStoreChange)
  start()
  return () => {
    listeners.delete(onStoreChange)
  }
}

/**
 * Subscribe outside React (the collection store switches its cache scope on
 * every auth change). Starts the shared subscription if nothing has yet.
 */
export function subscribeSupabaseIdentity(listener: () => void): () => void {
  try {
    return subscribe(listener)
  } catch {
    listeners.delete(listener)
    return () => {}
  }
}

function getSnapshot(): SupabaseTokenState {
  return state
}

// Prerender (src/prerender) has no browser session and must not start a
// subscription, so the server snapshot is a constant signed-out state.
function getServerSnapshot(): SupabaseTokenState {
  return SIGNED_OUT
}

function getAuthFailedSnapshot(): boolean {
  return authFailed
}

function getAuthFailedServerSnapshot(): boolean {
  return false
}

/**
 * True when the initial session read failed and nothing has answered since,
 * i.e. useSupabaseToken's "signed out" is a fallback, not a confirmed
 * anonymous visitor. Shares the store's single subscription.
 */
export function useSupabaseAuthFailed(): boolean {
  return useSyncExternalStore(subscribe, getAuthFailedSnapshot, getAuthFailedServerSnapshot)
}

/** useSupabaseAuthFailed read outside React (the deck follow-up waits on it). */
export function getSupabaseAuthFailed(): boolean {
  return authFailed
}

/**
 * The same snapshot, read outside React (the collection write fence compares
 * the user a write started under with the user it is about to commit for).
 * Starts the shared subscription if nothing has yet. A start that throws (no
 * usable auth client) leaves the snapshot as it was.
 */
export function getSupabaseIdentity(): SupabaseTokenState {
  try {
    start()
  } catch {
    // Identity stays unknown (loading); callers treat that as "cannot compare".
  }
  return state
}

/**
 * Reads the current Supabase session and keeps it fresh.
 *
 * Every caller shares one subscription and one snapshot (see the store above),
 * so two components can never disagree about who is signed in.
 */
export function useSupabaseToken(): SupabaseTokenState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
